import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Field, FieldLabel, Input } from "@akuna/ui";
import { resolveOffDayDecision, type OffDayPlanDay } from "@/lib/daycare-hotel/offDayDecision";
import { OffDayDecision } from "@/pages/daycare-hotel/reservation/OffDayDecision";
import { applyOffDayResolution, type OffDayResolution } from "@/pages/daycare-hotel/reservation/applyOffDayResolution";
import type { RecurringUsageRule } from "@/lib/daycare-hotel/stayPlans";
import { moveStayDay } from "./moveStayDay";

export interface RescheduleContract {
  id: string;
  usageRule: RecurringUsageRule | null;
  usageLimit: number | null;
  fixedWeekdays: number[];
  /** Âncora do ciclo, para a prévia das próximas datas ao mudar os dias. */
  firstDueOn?: string | null;
  /** Quinzenal: 2, contado a partir de `startsOn`. */
  weekInterval?: number;
  startsOn?: string;
}

export interface RescheduleStayDayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayId: string;
  petName: string;
  currentDate: string;
  contract: RescheduleContract | null;
  planDays: OffDayPlanDay[];
  onDone: () => void;
}

export function RescheduleStayDayDialog({
  open,
  onOpenChange,
  dayId,
  petName,
  currentDate,
  contract,
  planDays,
  onDone,
}: RescheduleStayDayDialogProps) {
  const { t } = useTranslation("daycareHotel");
  const [newDate, setNewDate] = useState(currentDate);
  const [resolution, setResolution] = useState<OffDayResolution | null>(null);

  // Enquanto o campo de data está vazio ou pela metade não dá para decidir
  // nada: `resolveOffDayDecision` exige uma data ISO completa.
  const dateIsComplete = /^\d{4}-\d{2}-\d{2}$/.test(newDate);
  const isMatricula = contract?.usageRule === "per_week" && contract.fixedWeekdays.length > 0;
  const isOffDay = Boolean(
    isMatricula && contract && dateIsComplete
      && resolveOffDayDecision({
        targetDate: newDate,
        fixedWeekdays: contract.fixedWeekdays,
        planDays,
        weekInterval: contract.weekInterval,
        startsOn: contract.startsOn,
      }).isOffDay,
  );

  const confirm = async () => {
    if (!dateIsComplete) return;
    if (isOffDay && contract) {
      if (!resolution) return;
      const outcome = await applyOffDayResolution({
        contractId: contract.id,
        fixedWeekdays: contract.fixedWeekdays,
        targetDate: newDate,
        usageLimit: contract.usageLimit,
        resolution,
      });
      // Mudar os dias já gera a diária na data nova: mover por cima duplicaria.
      if (outcome.next === "move") {
        await moveStayDay({ dayId, toDate: newDate, contractId: contract.id });
      }
      onDone();
      return;
    }
    await moveStayDay({ dayId, toDate: newDate, contractId: contract?.id ?? null });
    onDone();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("reschedule.title", { pet: petName })}
      closeLabel={t("common.cancel")}
      footer={
        <Button type="button" disabled={!dateIsComplete || (isOffDay && !resolution)} onClick={() => void confirm()}>
          {t("reschedule.confirm")}
        </Button>
      }
    >
      <div className="grid gap-4">
        <Field>
          <FieldLabel htmlFor="reschedule-date">{t("reschedule.newDateLabel")}</FieldLabel>
          <Input
            id="reschedule-date"
            type="date"
            value={newDate}
            onChange={(event) => {
              setNewDate(event.target.value);
              setResolution(null);
            }}
          />
        </Field>
        {isOffDay && contract ? (
          <OffDayDecision
            key={newDate}
            mode="move"
            targetDate={newDate}
            movingDate={currentDate}
            petName={petName}
            fixedWeekdays={contract.fixedWeekdays}
            planDays={planDays}
            planAnchor={contract.firstDueOn ?? currentDate}
            weekInterval={contract.weekInterval}
            planStartsOn={contract.startsOn}
            value={resolution}
            onChange={setResolution}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
